import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { mutationCreateCaseCluster } from '@/features/caseCluster/api/mutationCreateCaseCluster';
import { mutationUpdateCaseCluster } from '@/features/caseCluster/api/mutationUpdateCaseCluster';
import { useCaseListQuery } from '@/features/caseCluster/hooks/useCaseListQuery';
import { useAllClientListQuery } from '@/features/clients/hooks/useAllClientListQuery';
import { CaseCluster } from '@/services/API';
import { AppSpinner } from '@/ui/AppSpinner';
import ItemList from '@/ui/ItemList/ItemList';
import { useFormFieldArrayHelpers } from '@/utils/hooks/useFormFieldArrayHelpers';
import { isQueryFinishedLoading } from '@/utils/isQueryFinishedLoading';
import { assertIsTruthy } from '@/utils/types/type-assertion/generic/assertIsTruthy';

type Props = {
  data: CaseCluster | null;
  closeForm: () => void;
};

type FormData = {
  name: string;
  clientIDs: string[];
  selectedClientID: string | null;
  caseIDs: string[];
  selectedCaseID: string | null;
};

const CreateCaseClusterForm = ({ data, closeForm }: Props) => {
  const isEdit = !!data;
  const caseList = useCaseListQuery();
  const clientList = useAllClientListQuery();
  const { t } = useTranslation();
  const toast = useToast();

  const form = useForm<FormData>({
    defaultValues: {
      selectedCaseID: undefined,
      caseIDs: [],
      selectedClientID: undefined,
      clientIDs: [],
      name: '',
    },
  });
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = form;

  const caseListOfSelectedClients = useMemo(() => {
    if (!caseList.data || !clientList.data) {
      return [];
    }
    const currentClientIDs = watch('clientIDs') ?? [];
    return (
      caseList.data?.filter((caseItem) =>
        currentClientIDs.some((selectedClientID) => {
          const client = clientList.data.find((client) => client.id === selectedClientID)!;

          return caseItem.clientName === client.clientName;
        })
      ) ?? []
    );
  }, [caseList.data, clientList.data, watch('clientIDs')]);

  const {
    handleAdd: handleAddClient,
    handleRemove: handleRemoveClient,
    getAvailableItems: getAvailableClients,
  } = useFormFieldArrayHelpers({
    form,
    arrayFieldName: 'clientIDs',
    selectorFieldName: 'selectedClientID',
    allElements: clientList.data ?? [],
    onRemoveInterceptor: (id, currentArrayElements, expectedNewArray) => {
      if (!caseList.data || !clientList.data) {
        // NOTE: not done loading, so don't allow the update
        return currentArrayElements;
      }
      const client = clientList.data.find((client) => client.id === id)!;
      const currentCaseIDs = form.getValues('caseIDs') ?? [];
      const cases = currentCaseIDs.map((caseId) =>
        caseList.data!.find((caseItem) => caseItem.id === caseId)
      );
      const casesPartOfClientGettingRemoved = cases.filter((caseItem) => {
        return caseItem?.clientName === client.clientName;
      });

      if (isEdit) {
        // NOTE: only allow removing a client if there are no cases of that client in the caseIDs array
        if (casesPartOfClientGettingRemoved.length > 0) {
          toast({
            title: 'Cannot remove client',
            description: 'There are cases of this client in the case cluster.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return currentArrayElements;
        }
      }
      // NOTE: if the user removes a client, we remove the cases of that client from the caseIDs array
      const caseIDsToKeep = currentCaseIDs.filter((caseId) =>
        casesPartOfClientGettingRemoved.every((caseItem) => caseItem?.id !== caseId)
      );
      form.setValue('caseIDs', caseIDsToKeep);
      return expectedNewArray;
    },
  });
  const {
    handleAdd: handleAddCase,
    handleRemove: handleRemoveCase,
    getAvailableItems: getAvailableCases,
  } = useFormFieldArrayHelpers({
    form,
    arrayFieldName: 'caseIDs',
    selectorFieldName: 'selectedCaseID',
    allElements: caseListOfSelectedClients ?? [],
  });

  const currentClientIDs = watch('clientIDs') ?? [];
  const currentCaseIDs = watch('caseIDs') ?? [];

  // --------------------- handlers ------------------------

  const onSubmit = async (values: FormData) => {
    if (isEdit) {
      assertIsTruthy(data);
      await mutationUpdateCaseCluster({
        input: {
          id: data.id,
          name: values.name,
          clientIDs: values.clientIDs,
        },
      });
    } else {
      await mutationCreateCaseCluster({
        input: {
          name: values.name,
          cases: values.caseIDs,
          clientIDs: values.clientIDs,
        },
      });
    }
    closeForm();
    reset();
  };

  const onClientChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      form.setValue('selectedClientID', e.target.value);
      handleAddClient();
      form.setValue('selectedClientID', '');
    },
    [form, handleAddClient]
  );

  const onCaseChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      form.setValue('selectedCaseID', e.target.value);
      handleAddCase();
      form.setValue('selectedCaseID', '');
    },
    [form, handleAddCase]
  );

  // --------------------- effects ------------------------

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        clientIDs: data.clientIDs || [],
        caseIDs: data.cases || [],
      });
    }
  }, [data]);

  // NOTE: when data changes, set the name of the case cluster
  useEffect(() => {
    if (!clientList.data || !caseList.data) {
      return;
    }
    const selectedClients = currentClientIDs.map((clientId) =>
      clientList.data.find((elem) => elem.id === clientId)
    );
    form.setValue(
      'name',
      `${selectedClients.map((el) => el?.clientName).join(', ')} - ${currentCaseIDs.join(', ')}`
    );
  }, [currentClientIDs, currentCaseIDs]);

  if (!isQueryFinishedLoading(caseList) || !isQueryFinishedLoading(clientList)) {
    return <AppSpinner />;
  }

  // --------------------- render ------------------------
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack gap="10px" pb="10px">
        {/* NAME */}
        <FormControl isRequired isInvalid={Boolean(errors.name)} isDisabled={true}>
          <FormLabel fontSize="sm" fontWeight="normal" mb="1">
            {t('admin.caseClusters.createForm.field.name')}:
          </FormLabel>
          <Input type="text" {...register('name', { required: true })} placeholder="Name" />
          {errors.name && (
            <FormHelperText color="red">
              {t('admin.caseClusters.createForm.name.error')}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="normal" mb="1">
            {t('admin.caseClusters.createForm.field.clients')}
          </FormLabel>

          <HStack>
            <Controller
              name="selectedClientID"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder={
                    t('dmin.caseClusters.createForm.field.clients.placeholder') || 'Select'
                  }
                  {...field}
                  value={field.value ?? undefined}
                  onChange={onClientChange}
                >
                  {getAvailableClients().map((elem) => (
                    <option key={elem.id} value={elem.id}>
                      {elem.clientName}
                    </option>
                  ))}
                </Select>
              )}
            />
          </HStack>

          <VStack p={2} align="flex-start">
            {currentClientIDs.length && (
              <ItemList
                itemList={currentClientIDs.map((clientId) => {
                  const elem = clientList.data?.find((elem) => elem.id === clientId);
                  return {
                    id: clientId,
                    value: elem?.clientName || 'N/A',
                  };
                })}
                removeItemFunc={handleRemoveClient}
              />
            )}
          </VStack>
        </FormControl>

        {/* CASE LIST */}

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="normal" mb="1">
            {t('admin.caseClusters.createForm.field.cases')}
          </FormLabel>

          <HStack>
            <Controller
              name="selectedCaseID"
              control={control}
              disabled={isEdit}
              render={({ field }) => (
                <Select
                  placeholder={
                    t('dmin.caseClusters.createForm.field.cases.placeholder') || 'Select'
                  }
                  {...field}
                  value={field.value ?? undefined}
                  onChange={onCaseChange}
                  isDisabled={isEdit}
                >
                  {getAvailableCases().map((elem) => (
                    <option key={elem.id} value={elem.id}>
                      {elem.name}
                    </option>
                  ))}
                </Select>
              )}
            />
          </HStack>

          <VStack p={2} align="flex-start">
            {currentCaseIDs.length && (
              <ItemList
                itemList={currentCaseIDs.map((caseId) => {
                  const elem = caseList.data?.find((elem) => elem.id === caseId);
                  return {
                    id: caseId,
                    value: elem?.name || 'N/A',
                  };
                })}
                removeItemFunc={handleRemoveCase}
                disableAllRemove={isEdit}
              />
            )}
          </VStack>
        </FormControl>
      </VStack>

      {/* SUBMIT */}
      <Button
        isDisabled={currentCaseIDs.length < 1 || currentClientIDs.length < 1}
        onClick={handleSubmit(onSubmit)}
        p="2"
        mx="2"
        w="20%"
        background="blue.400"
        color="white"
        variant="solid"
      >
        {isEdit ? t('common.update') : t('common.create')}
      </Button>
    </form>
  );
};

export default CreateCaseClusterForm;
